import {
  SecurityDateFilter,
  SecurityRouteModel,
  SecurityTravelMode,
  PointOfInterestModel,
  SecurityTrafficLevel,
  SecurityRouteStepModel,
  SecurityRouteTripModel,
  SecurityTransitStepModel,
  SecurityRouteOptionModel,
  SecurityCountryFlagModel,
  SecurityCountryFlagResultModel,
  SecurityRouteOptionCardModel,
  SecurityMappingStepModel,
  SecurityTravelModeOptionModel,
  SecurityDateFilterOptionModel,
} from "src/app/store/models/security-mapping.model";
import { TranslateService } from "@ngx-translate/core";
import { MatDateFormats } from "@angular/material/core";

const ASSET_PATH = "assets/settings/security-mapping";

// Figma says 20; raised to fit Google-style names "place, area" (developer's request 2026-09-30)
export const SECURITY_NAME_MAX_LENGTH = 60;
export const SECURITY_DESCRIPTION_MAX_LENGTH = 50;
// Radius box limit (mockup v1.38.78); the slider only goes to SECURITY_RADIUS_SLIDER_MAX_METERS
export const SECURITY_RADIUS_MAX_METERS = 50000;
export const SECURITY_PERIMETER_MIN_POINTS = 3;
// At zoom 18 the 50 m max radius is ~100 px, as in Figma (27034:10475); at 14 it is ~6 px
export const SECURITY_AREA_FOCUS_ZOOM = 18;

// Colour from Figma token Secondary-04_Alert_Stop: risk areas of an unsafe category
// (master data isUnsafe); other risk areas are SECURITY_DRAWN_GREEN (developer's request
// 2026-10-08)
export const SECURITY_ZONE_RED = "#EF0015";
// Perimeter drawing as Site setup's zone fencing (developer's request 2026-10-07):
// yellow line and dots while drawing; once closed, red or green by the category
export const SECURITY_DRAWING_YELLOW = "#FFD700";
export const SECURITY_DRAWN_GREEN = "#76FF03";

// Route line from Figma (27013:9554): Extra colors/Primary-05, 6px
export const SECURITY_ROUTE_COLOR = "#B2E000";
export const SECURITY_ROUTE_STROKE_WEIGHT = 6;
// A route through a risk area (Figma 27003:8648): light red, 4px
export const SECURITY_RISKY_ROUTE_COLOR = "#FF8F8F";
export const SECURITY_ALTERNATIVE_ROUTE_STROKE_WEIGHT = 4;
// Each stop adds one Directions request (legs are routed one by one)
export const SECURITY_ROUTE_MAX_STOPS = 8;

// date-fns tokens for the date-fns adapter: "1 Jun 2026", as in Figma (27034:13300)
export const SECURITY_DATE_FORMATS: MatDateFormats = {
  parse: { dateInput: "d MMM yyyy" },
  display: {
    dateInput: "d MMM yyyy",
    monthYearLabel: "MMM yyyy",
    dateA11yLabel: "d MMMM yyyy",
    monthYearA11yLabel: "MMMM yyyy",
  },
};

export const SECURITY_MAPPING_STEPS: SecurityMappingStepModel[] = [
  {
    iconWidth: 22.6942,
    iconHeight: 29,
    type: "PointsAndAreas",
    isIconMirrored: false,
    icon: `${ASSET_PATH}/points-and-areas-count.svg`,
    titleKey: "COUNTRY_SECURITY.STEP_POINTS_AND_AREAS",
    addActionKey: "COUNTRY_SECURITY.ADD_POINTS_AND_AREAS",
    instructionKeys: [
      "COUNTRY_SECURITY.POINTS_AND_AREAS_INSTRUCTION_1",
      "COUNTRY_SECURITY.POINTS_AND_AREAS_INSTRUCTION_2",
      "COUNTRY_SECURITY.POINTS_AND_AREAS_INSTRUCTION_3",
    ],
  },
  {
    iconWidth: 30.6754,
    iconHeight: 29.15,
    isIconMirrored: true,
    type: "Routes",
    icon: `${ASSET_PATH}/step-routes.svg`,
    titleKey: "COUNTRY_SECURITY.STEP_ROUTES",
    addActionKey: "COUNTRY_SECURITY.ADD_ROUTE",
    instructionKeys: [
      "COUNTRY_SECURITY.ROUTES_INSTRUCTION_1",
      "COUNTRY_SECURITY.ROUTES_INSTRUCTION_2",
      "COUNTRY_SECURITY.ROUTES_INSTRUCTION_3",
    ],
  },
];

export function isValidLatitude(value: number): boolean {
  return Number.isFinite(value) && value >= -90 && value <= 90;
}

export function isValidLongitude(value: number): boolean {
  return Number.isFinite(value) && value >= -180 && value <= 180;
}

// crypto.randomUUID is missing on iOS < 15.4 WebViews: fall back to a time + random id
export function createSecurityItemId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function roundCoordinate(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

// Name for a route point picked on the map: its coordinates
export function formatCoordinates(position: google.maps.LatLngLiteral): string {
  return `${roundCoordinate(position.lat)}, ${roundCoordinate(position.lng)}`;
}

// Google place names can be longer than the name field: cut at a word when possible
export function fitSecurityName(
  name: string,
  maxLength: number = SECURITY_NAME_MAX_LENGTH,
): string {
  const trimmed = name.trim();
  if (trimmed.length <= maxLength) return trimmed;
  const cut = trimmed.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace >= maxLength / 2 ? cut.slice(0, lastSpace) : cut)
    .replace(/[,\s]+$/, "")
    .trim();
}

// Hours and minutes for "1 h 5 min" / "25 min" (at least 1 minute, like Google Maps)
export function splitDuration(durationInSeconds: number): {
  hours: number;
  minutes: number;
} {
  const totalMinutes = Math.max(1, Math.round(durationInSeconds / 60));
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

// Route form tabs, in Google Maps order
export const SECURITY_TRAVEL_MODES: SecurityTravelModeOptionModel[] = [
  {
    mode: "driving",
    icon: "assets/settings/security-mapping/driving.svg",
    labelKey: "COUNTRY_SECURITY.DRIVING",
  },
  {
    mode: "transit",
    icon: "assets/settings/security-mapping/transit.svg",
    labelKey: "COUNTRY_SECURITY.TRANSIT",
  },
  {
    mode: "walking",
    icon: "assets/settings/security-mapping/walking.svg",
    labelKey: "COUNTRY_SECURITY.WALKING",
  },
];

export function getTravelModeOption(
  mode: SecurityTravelMode,
): SecurityTravelModeOptionModel {
  return (
    SECURITY_TRAVEL_MODES.find((option) => option.mode === mode) ??
    SECURITY_TRAVEL_MODES[0]
  );
}

// Google Maps colours the driving time by traffic: green, orange, red
export const SECURITY_TRAFFIC_COLOR_CLASSES: Record<
  SecurityTrafficLevel,
  string
> = {
  light: "text-[#009146]",
  moderate: "text-[#E37400]",
  heavy: "text-[#EF0015]",
};

// "25 min", "1 h", "1 h 5 min"
export function formatSecurityDuration(
  translate: TranslateService,
  durationInSeconds: number,
): string {
  const { hours, minutes } = splitDuration(durationInSeconds);
  if (hours === 0) {
    return translate.instant("COUNTRY_SECURITY.DURATION_MINUTES", { minutes });
  }
  return minutes === 0
    ? translate.instant("COUNTRY_SECURITY.DURATION_HOURS", { hours })
    : translate.instant("COUNTRY_SECURITY.DURATION_HOURS_MINUTES", {
        hours,
        minutes,
      });
}

// Google Directions step → plain text step. Google's instructions are HTML ("Turn
// <b>left</b>…", with extra lines in <div>s): parsed, never inserted as HTML.
export function toSecurityRouteStep(
  step: google.maps.DirectionsStep,
): SecurityRouteStepModel {
  const body = new DOMParser().parseFromString(
    step.instructions ?? "",
    "text/html",
  ).body;
  const notes = Array.from(body.querySelectorAll("div"))
    .map((note) => note.textContent?.trim() ?? "")
    .filter(Boolean);
  body.querySelectorAll("div").forEach((note) => note.remove());
  const transit = step.transit;
  return {
    maneuver: step.maneuver ?? "",
    pointCount: step.path.length,
    mode: toSecurityTravelMode(step.travel_mode),
    transit: transit
      ? {
          lineName: transit.line?.short_name || transit.line?.name || "",
          vehicleType: transit.line?.vehicle?.type ?? "",
          vehicleName: transit.line?.vehicle?.name ?? "",
          headsign: transit.headsign ?? "",
          stopCount: transit.num_stops ?? 0,
          arrivalStop: transit.arrival_stop?.name ?? "",
          departureStop: transit.departure_stop?.name ?? "",
        }
      : null,
    position: step.start_location.toJSON(),
    note: notes.join(" ") || null,
    distanceInMeters: step.distance?.value ?? 0,
    durationInSeconds: step.duration?.value ?? 0,
    instruction: (body.textContent ?? "").replace(/\s+/g, " ").trim(),
  };
}

function toSecurityTravelMode(
  travelMode: google.maps.TravelMode | google.maps.TravelModeString,
): SecurityTravelMode {
  switch (travelMode) {
    case google.maps.TravelMode.TRANSIT:
      return "transit";
    case google.maps.TravelMode.WALKING:
      return "walking";
    default:
      return "driving";
  }
}

// Ionicon of a step: the vehicle of a ride, a walker for a transit route's walk,
// else the turn arrow
export function getSecurityStepIcon(step: SecurityRouteStepModel): string {
  if (step.transit) return getTransitVehicleIcon(step.transit.vehicleType);
  if (step.mode === "walking" && !step.maneuver) return "walk";
  return getManeuverIcon(step.maneuver);
}

// Ride details under a transit step ("Line 12 · 5 stops · A → B"); else Google's note
export function getSecurityStepNote(
  translate: TranslateService,
  step: SecurityRouteStepModel,
): string | null {
  if (!step.transit) return step.note;
  return translate.instant("COUNTRY_SECURITY.TRANSIT_STEP_NOTE", {
    line: step.transit.lineName,
    stops: step.transit.stopCount,
    to: step.transit.arrivalStop,
    from: step.transit.departureStop,
  });
}

function getTransitVehicleIcon(vehicleType: string): string {
  switch (vehicleType) {
    case "SUBWAY":
    case "METRO_RAIL":
    case "MONORAIL":
      return "subway";
    case "RAIL":
    case "HEAVY_RAIL":
    case "COMMUTER_TRAIN":
    case "HIGH_SPEED_TRAIN":
    case "LONG_DISTANCE_TRAIN":
    case "TRAM":
      return "train";
    case "FERRY":
      return "boat";
    default:
      return "bus";
  }
}

// Ionicon for a Google maneuver, like the arrows in Google Maps' directions
export function getManeuverIcon(maneuver: string): string {
  if (maneuver.startsWith("uturn")) return "arrow-undo-outline";
  if (maneuver.startsWith("roundabout")) return "sync-outline";
  if (maneuver.endsWith("left")) return "return-up-back-outline";
  if (maneuver.endsWith("right")) return "return-up-forward-outline";
  return "arrow-up-outline";
}

// "39 m" / "1.2 km"
export function formatSecurityDistance(
  translate: TranslateService,
  distanceInMeters: number,
): string {
  return distanceInMeters < 1000
    ? translate.instant("COUNTRY_SECURITY.DISTANCE_M", {
        distance: Math.round(distanceInMeters),
      })
    : translate.instant("COUNTRY_SECURITY.DISTANCE_KM", {
        distance: (distanceInMeters / 1000).toFixed(1),
      });
}

// Delay share over the usual time for moderate / heavy traffic (Google Maps colours
// times the same way: green, orange, red). Delays under the minimum count as light.
const MODERATE_TRAFFIC_RATIO = 1.15;
const HEAVY_TRAFFIC_RATIO = 1.4;
const MIN_TRAFFIC_DELAY_SECONDS = 120;

export function getSecurityTrafficLevel(
  usualSeconds: number,
  trafficSeconds: number,
): SecurityTrafficLevel {
  const delayInSeconds = trafficSeconds - usualSeconds;
  if (usualSeconds <= 0 || delayInSeconds < MIN_TRAFFIC_DELAY_SECONDS) {
    return "light";
  }
  const ratio = trafficSeconds / usualSeconds;
  if (ratio >= HEAVY_TRAFFIC_RATIO) return "heavy";
  return ratio >= MODERATE_TRAFFIC_RATIO ? "moderate" : "light";
}

// ** Road cards (route form's road list and the saved routes panel)
const NEUTRAL_TEXT_CLASS = "text-[#1A1C1C]";
// Up to this share longer than the shortest road: "Slightly longer"
const SLIGHTLY_LONGER_RATIO = 0.15;
const TRAFFIC_NOTE_KEYS: Record<SecurityTrafficLevel, string> = {
  light: "COUNTRY_SECURITY.ROUTE_NOTE_LIGHT_TRAFFIC",
  moderate: "COUNTRY_SECURITY.ROUTE_NOTE_MODERATE_TRAFFIC",
  heavy: "COUNTRY_SECURITY.ROUTE_NOTE_HEAVY_TRAFFIC",
};

// Time used for a road: with traffic when known (driving), else Google's usual time
export function getSecurityTravelSeconds(
  option: SecurityRouteOptionModel,
): number {
  return option.durationInTrafficInSeconds ?? option.durationInSeconds;
}

// One leg's roads as cards: "via …" title, a note comparing it with the leg's other
// roads (or the risk areas it crosses), time with traffic and distance
export function buildSecurityRouteOptionCards(
  translate: TranslateService,
  options: SecurityRouteOptionModel[],
  selectedIndex: number | undefined,
): SecurityRouteOptionCardModel[] {
  const fastestIndex = findLowestIndex(options, getSecurityTravelSeconds);
  const shortestIndex = findLowestIndex(
    options,
    (option) => option.distanceInMeters,
  );
  const shortestMeters = options[shortestIndex]?.distanceInMeters ?? 0;
  // Recommended = the fastest road / trip (time with traffic) outside every unsafe
  // zone, also on saved or rechecked roads (developer's request 2026-10-14); none:
  // the first one, no badge
  const recommendedIndex = findRecommendedIndex(options);
  const firstIndex = Math.max(recommendedIndex, 0);
  const cards = options.map((option, optionIndex) => {
    const trafficSeconds = option.durationInTrafficInSeconds;
    const trafficLevel =
      trafficSeconds === null
        ? null
        : getSecurityTrafficLevel(option.durationInSeconds, trafficSeconds);
    return {
      optionIndex,
      isSafest: option.isSafest,
      isSafe: !option.touchesRiskAreas,
      isSelected: optionIndex === selectedIndex,
      distanceInKm: (option.distanceInMeters / 1000).toFixed(1),
      title: getSecurityRouteOptionTitle(translate, option, optionIndex),
      durationText: formatSecurityDuration(
        translate,
        getSecurityTravelSeconds(option),
      ),
      durationColorClass: trafficLevel
        ? SECURITY_TRAFFIC_COLOR_CLASSES[trafficLevel]
        : NEUTRAL_TEXT_CLASS,
      // Unsafe roads get the same comparison note as the others; the zones they pass
      // through are only in the red line (mockup v1.38.85, developer's screenshot
      // 2026-10-26: the note repeated it). A lone unsafe road has nothing to compare
      note:
        option.touchesRiskAreas && options.length === 1
          ? ""
          : getSafeNote(
              translate,
              optionIndex === recommendedIndex,
              optionIndex === fastestIndex && options.length > 1,
              optionIndex === shortestIndex && options.length > 1,
              option.distanceInMeters - shortestMeters,
              shortestMeters,
              trafficLevel,
            ),
      riskOverlapText: option.touchesRiskAreas
        ? getRiskOverlapText(translate, option)
        : null,
      // Shown first: the Recommended card, else (every road unsafe) Google's first
      isFirst: optionIndex === firstIndex,
      isRecommended: optionIndex === recommendedIndex,
    };
  });
  // Recommended always first (developer's request 2026-10-09), then the safest: roads
  // marked Safe, then roads outside risk areas, then the fewest risk areas; Google's
  // order otherwise (sort is stable). optionIndex still points at the trip / road.
  return cards.sort(
    (first, second) =>
      Number(second.isFirst) - Number(first.isFirst) ||
      getSafetyRank(options[first.optionIndex]) -
        getSafetyRank(options[second.optionIndex]),
  );
}

// "via Bir Uttam Ziaur Rahman Rd" (Google's summary, else the main roads or the transit
// lines of its steps), or "Route 2" when there is no name at all
export function getSecurityRouteOptionTitle(
  translate: TranslateService,
  option: SecurityRouteOptionModel,
  optionIndex: number,
): string {
  const name = option.summary || getStepsRouteName(option.steps);
  return name
    ? translate.instant("COUNTRY_SECURITY.ROUTE_VIA", { name })
    : translate.instant("COUNTRY_SECURITY.ROUTE_OPTION", {
        number: optionIndex + 1,
      });
}

// Lower is safer: marked Safest 0, outside risk areas 1, else 2 + risk areas crossed
function getSafetyRank(option: SecurityRouteOptionModel): number {
  if (option.isSafest) return 0;
  if (!option.touchesRiskAreas) return 1;
  return 2 + getRiskZoneCount(option);
}

// Google has no summary for transit (and some short roads): transit → the lines ridden
// ("Bus 12 → Metro rail M1"); others → the two roads travelled longest, as Google's
// own summaries ("Mirpur Rd and Road 5")
function getStepsRouteName(steps: SecurityRouteStepModel[]): string {
  const rides = steps
    .map((step) => step.transit)
    .filter((transit): transit is SecurityTransitStepModel => !!transit)
    .map((transit) =>
      [transit.vehicleName, transit.lineName].filter(Boolean).join(" "),
    )
    .filter(Boolean);
  if (rides.length > 0) return [...new Set(rides)].join(" → ");

  const metersByRoad = new Map<string, number>();
  steps.forEach((step) => {
    const road = getStepRoadName(step.instruction);
    if (!road) return;
    metersByRoad.set(
      road,
      (metersByRoad.get(road) ?? 0) + step.distanceInMeters,
    );
  });
  return [...metersByRoad.entries()]
    .sort((first, second) => second[1] - first[1])
    .slice(0, 2)
    .map(([road]) => road)
    .join(" and ");
}

// The road of a Google instruction: "Head north on Mirpur Rd toward Road 5" → "Mirpur Rd",
// "Turn left onto Road 5" → "Road 5"; null when it names none (e.g. "Bus towards …")
function getStepRoadName(instruction: string): string | null {
  const match =
    /\b(?:on|onto)\s+(.+?)(?:\s+toward\b.*|\s+Pass by\b.*|\s*\/.*)?$/.exec(
      instruction,
    );
  const road = match?.[1]?.trim();
  return road && road.length <= 60 ? road : null;
}

// A saved route's roads per leg. Routes saved before the roads were kept: one road per
// leg rebuilt from the saved path (distance shared out by the legs' times).
export function getSavedRouteLegOptions(
  route: SecurityRouteModel,
): SecurityRouteOptionModel[][] {
  if (route.legOptions.length > 0) return route.legOptions;
  const { routePath } = route;
  let pathIndex = 0;
  return routePath.legs.map((leg) => {
    const path = routePath.path.slice(pathIndex, pathIndex + leg.pointCount);
    pathIndex += leg.pointCount;
    const timeShare =
      routePath.durationInSeconds > 0
        ? leg.durationInSeconds / routePath.durationInSeconds
        : 1 / routePath.legs.length;
    return [
      {
        path,
        summary: "",
        steps: leg.steps ?? [],
        viaPoint: leg.viaPoint,
        isSafest: route.isSafestRoute,
        durationInTrafficInSeconds: null,
        durationInSeconds: leg.durationInSeconds,
        touchesRiskAreas: !routePath.avoidsRiskAreas,
        distanceInMeters: Math.round(routePath.distanceInMeters * timeShare),
      },
    ];
  });
}

// The first road outside unsafe zones is the recommendation; the others are
// described by how they compare (fastest, shortest, longer), then their traffic
function getSafeNote(
  translate: TranslateService,
  isRecommended: boolean,
  isFastest: boolean,
  isShortest: boolean,
  extraMeters: number,
  shortestMeters: number,
  trafficLevel: SecurityTrafficLevel | null,
): string {
  if (isRecommended) {
    return translate.instant("COUNTRY_SECURITY.ROUTE_NOTE_RECOMMENDED");
  }
  let comparison: string;
  if (isFastest) {
    comparison = translate.instant("COUNTRY_SECURITY.ROUTE_NOTE_FASTEST");
  } else if (isShortest) {
    comparison = translate.instant("COUNTRY_SECURITY.ROUTE_NOTE_SHORTEST");
  } else if (extraMeters <= shortestMeters * SLIGHTLY_LONGER_RATIO) {
    comparison = translate.instant(
      "COUNTRY_SECURITY.ROUTE_NOTE_SLIGHTLY_LONGER",
    );
  } else {
    comparison = translate.instant("COUNTRY_SECURITY.ROUTE_NOTE_LONGER", {
      distance: (extraMeters / 1000).toFixed(1),
    });
  }
  return trafficLevel
    ? `${comparison}, ${translate.instant(TRAFFIC_NOTE_KEYS[trafficLevel])}`
    : comparison;
}

function findLowestIndex(
  options: SecurityRouteOptionModel[],
  getValue: (option: SecurityRouteOptionModel) => number,
): number {
  return options.reduce(
    (best, option, index) =>
      getValue(option) < getValue(options[best]) ? index : best,
    0,
  );
}

// ** Whole-trip routes (developer's request 2026-10-01): with stops, the road list shows
// trips start → end instead of each leg's roads, so there is one safest suggestion.
// Trips: Google's recommended roads, the safest, the fastest, the shortest, the one
// the user marked safest and the one in use (duplicates dropped). Without stops: every
// road of the single leg, in Google's order.
export function buildSecurityRouteTrips(
  legOptions: SecurityRouteOptionModel[][],
  selectedOptionIndexes: number[],
): SecurityRouteTripModel[] {
  if (legOptions.length === 0) return [];
  if (legOptions.length === 1) {
    return legOptions[0].map((option, optionIndex) => ({
      option,
      optionIndexes: [optionIndex],
    }));
  }
  const pick = (
    choose: (options: SecurityRouteOptionModel[]) => number,
  ): number[] => legOptions.map(choose);
  const markedSafest = legOptions.every((options) =>
    options.some((option) => option.isSafest),
  )
    ? pick((options) => options.findIndex((option) => option.isSafest))
    : null;
  const candidates = [
    pick(() => 0),
    pick(findSafestRoadIndex),
    pick((options) => findLowestIndex(options, getSecurityTravelSeconds)),
    pick((options) =>
      findLowestIndex(options, (option) => option.distanceInMeters),
    ),
    markedSafest,
    selectedOptionIndexes.length === legOptions.length
      ? selectedOptionIndexes
      : null,
  ];
  const tripsByKey = new Map<string, number[]>();
  candidates.forEach((optionIndexes) => {
    const isValid =
      !!optionIndexes &&
      optionIndexes.every(
        (optionIndex, legIndex) => !!legOptions[legIndex]?.[optionIndex],
      );
    if (isValid) tripsByKey.set(optionIndexes.join(","), optionIndexes);
  });
  return [...tripsByKey.values()].map((optionIndexes) => ({
    optionIndexes,
    option: combineTripRoads(legOptions, optionIndexes),
  }));
}

export function isSameTrip(first: number[], second: number[]): boolean {
  return (
    first.length === second.length &&
    first.every((optionIndex, legIndex) => optionIndex === second[legIndex])
  );
}

// Outside every risk area and shortest; else the fewest risk areas, then shortest
function findSafestRoadIndex(options: SecurityRouteOptionModel[]): number {
  const riskCount = (option: SecurityRouteOptionModel): number =>
    option.touchesRiskAreas ? getRiskZoneCount(option) + 1 : 0;
  return options.reduce(
    (best, option, index) =>
      riskCount(option) < riskCount(options[best]) ||
      (riskCount(option) === riskCount(options[best]) &&
        option.distanceInMeters < options[best].distanceInMeters)
        ? index
        : best,
    0,
  );
}

// One trip as a single road for its card: summaries, sums, risk areas of all legs
function combineTripRoads(
  legOptions: SecurityRouteOptionModel[][],
  optionIndexes: number[],
): SecurityRouteOptionModel {
  const roads = optionIndexes.map(
    (optionIndex, legIndex) => legOptions[legIndex][optionIndex],
  );
  const sum = (getValue: (road: SecurityRouteOptionModel) => number): number =>
    roads.reduce((total, road) => total + getValue(road), 0);
  const hasTraffic = roads.every(
    (road) => road.durationInTrafficInSeconds !== null,
  );
  return {
    viaPoint: null,
    path: roads.flatMap((road) => road.path),
    steps: roads.flatMap((road) => road.steps),
    isSafest: roads.every((road) => road.isSafest),
    distanceInMeters: sum((road) => road.distanceInMeters),
    durationInSeconds: sum((road) => road.durationInSeconds),
    touchesRiskAreas: roads.some((road) => road.touchesRiskAreas),
    summary: [
      ...new Set(roads.map((road) => road.summary).filter(Boolean)),
    ].join(", "),
    riskAreaNames: [
      ...new Set(roads.flatMap((road) => road.riskAreaNames ?? [])),
    ],
    riskPointNames: [
      ...new Set(roads.flatMap((road) => road.riskPointNames ?? [])),
    ],
    // Only when every leg has ids, else the trip falls back to names
    ...(roads.every((road) => road.riskAreaIds)
      ? {
          riskAreaIds: [
            ...new Set(roads.flatMap((road) => road.riskAreaIds ?? [])),
          ],
          riskPointIds: [
            ...new Set(roads.flatMap((road) => road.riskPointIds ?? [])),
          ],
        }
      : {}),
    durationInTrafficInSeconds: hasTraffic
      ? sum((road) => road.durationInTrafficInSeconds ?? 0)
      : null,
  };
}

// ** Date filter (toolbar Filter, developer's request 2026-10-02)
export const SECURITY_DATE_FILTERS: SecurityDateFilterOptionModel[] = [
  { filter: "all", labelKey: "ALL" },
  { filter: "current", labelKey: "COUNTRY_SECURITY.CURRENT" },
  { filter: "expired", labelKey: "DOC_EXPIRED" },
];

// Current: valid today (started, if it has a start date, and not ended); items without
// dates are always current. Expired: the end date is over. Dates count by whole days.
export function matchesSecurityDateFilter(
  item: PointOfInterestModel,
  filter: SecurityDateFilter,
  now: Date = new Date(),
): boolean {
  if (filter === "all") return true;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const endDay = item.endDate ? new Date(item.endDate) : null;
  endDay?.setHours(0, 0, 0, 0);
  const isExpired = !!endDay && endDay < today;
  if (filter === "expired") return isExpired;
  const startDay = item.startDate ? new Date(item.startDate) : null;
  startDay?.setHours(0, 0, 0, 0);
  const hasStarted = !startDay || startDay <= today;
  return hasStarted && !isExpired;
}

// "Overlaps with 1 unsafe AOI and 2 unsafe POIs" (developer's request 2026-10-05): each
// crossed zone counts as a POI or an AOI by its master data category kind; only the
// kinds crossed are named. Roads saved before have no POI names: all count as AOI
function getRiskOverlapText(
  translate: TranslateService,
  option: SecurityRouteOptionModel,
): string {
  const pointCount = getRiskPointCount(option);
  const areaCount = Math.max(0, getRiskZoneCount(option) - pointCount);
  // Roads saved without their zones: "Crosses risk areas"
  if (areaCount + pointCount === 0) {
    return translate.instant("COUNTRY_SECURITY.CROSSES_RISK_AREAS");
  }
  const { areaNames, pointNames } = getRiskZoneNames(option);
  const parts = [
    areaCount > 0
      ? getRiskPartText(translate, "AOI", areaCount, areaNames)
      : null,
    pointCount > 0
      ? getRiskPartText(translate, "POI", pointCount, pointNames)
      : null,
  ].filter((part): part is string => !!part);
  const items =
    parts.length === 2
      ? translate.instant("COUNTRY_SECURITY.LIST_TWO_ITEMS", {
          first: parts[0],
          second: parts[1],
        })
      : parts[0];
  // "Passes through 1 unsafe AOI (Dock Zone) and 2 unsafe POI (A, B)" (mockup
  // v1.38.85, developer's screenshot 2026-10-26)
  return translate.instant("COUNTRY_SECURITY.ROUTE_PASSES_THROUGH", { items });
}

// "2 unsafe AOI (Zone A, Zone B)"; the count alone ("2 unsafe AOIs") when the road
// kept no names
function getRiskPartText(
  translate: TranslateService,
  kind: "AOI" | "POI",
  count: number,
  names: string[],
): string {
  if (names.length > 0) {
    return translate.instant(`COUNTRY_SECURITY.UNSAFE_${kind}_NAMES`, {
      count,
      names: names.join(", "),
    });
  }
  return translate.instant(
    count === 1
      ? `COUNTRY_SECURITY.UNSAFE_${kind}_COUNT_ONE`
      : `COUNTRY_SECURITY.UNSAFE_${kind}_COUNT`,
    { count },
  );
}

// Names of the unsafe areas and points a road crosses, once per zone: by id (names
// are saved in the same order), or by name for roads found before 2026-10-06
function getRiskZoneNames(option: SecurityRouteOptionModel): {
  areaNames: string[];
  pointNames: string[];
} {
  const areaIds = option.riskAreaIds;
  const allNames = option.riskAreaNames ?? [];
  const pointNames = option.riskPointNames ?? [];
  if (!areaIds) {
    const uniquePointNames = [...new Set(pointNames)];
    return {
      pointNames: uniquePointNames,
      areaNames: [...new Set(allNames)].filter(
        (name) => !uniquePointNames.includes(name),
      ),
    };
  }
  const pointIds = option.riskPointIds ?? [];
  return {
    areaNames: getNamesById(areaIds, allNames, (id) => !pointIds.includes(id)),
    pointNames: getNamesById(pointIds, pointNames, () => true),
  };
}

function getNamesById(
  ids: string[],
  names: string[],
  isIncluded: (id: string) => boolean,
): string[] {
  const seenIds = new Set<string>();
  return ids.flatMap((id, index) => {
    const name = names[index]?.trim();
    if (!name || seenIds.has(id) || !isIncluded(id)) return [];
    seenIds.add(id);
    return [name];
  });
}

// Latitude / longitude in the point / area form: 4 decimals (≈ 11 m) for reading; the
// saved position keeps every decimal (developer's request 2026-10-06)
export const SECURITY_COORDINATE_FIELD_DECIMALS = 4;

export function formatCoordinateForField(value: number): string {
  return value.toFixed(SECURITY_COORDINATE_FIELD_DECIMALS);
}

// Risk zones (areas + unsafe points) a road crosses: by id, or by name for roads found
// before 2026-10-06 (developer's request: two zones can share a name)
function getRiskZoneCount(option: SecurityRouteOptionModel): number {
  return new Set(option.riskAreaIds ?? option.riskAreaNames ?? []).size;
}

// The unsafe points (POI) among them
function getRiskPointCount(option: SecurityRouteOptionModel): number {
  return new Set(
    option.riskAreaIds
      ? (option.riskPointIds ?? [])
      : (option.riskPointNames ?? []),
  ).size;
}

// ** Unsafe zones (developer's request 2026-10-08): only points / risk areas of an
// unsafe category (master data isUnsafe) make a road red, and only while current

export function getSecurityZoneColor(isUnsafe: boolean): string {
  return isUnsafe ? SECURITY_ZONE_RED : SECURITY_DRAWN_GREEN;
}

// Recommended = the fastest road / trip (time with traffic) outside every unsafe zone;
// -1 when every one crosses a zone
function findRecommendedIndex(options: SecurityRouteOptionModel[]): number {
  return options.reduce(
    (best, option, index) =>
      !option.touchesRiskAreas &&
      (best < 0 ||
        getSecurityTravelSeconds(option) <
          getSecurityTravelSeconds(options[best]))
        ? index
        : best,
    -1,
  );
}

// Road per leg of the Recommended trip (the card with the badge), so a saved route opens
// with it in use and bold like a new one (developer's request 2026-10-15); null when
// every trip crosses an unsafe zone
export function getRecommendedTripIndexes(
  legOptions: SecurityRouteOptionModel[][],
  selectedOptionIndexes: number[],
): number[] | null {
  const trips = buildSecurityRouteTrips(legOptions, selectedOptionIndexes);
  const recommendedIndex = findRecommendedIndex(
    trips.map((trip) => trip.option),
  );
  return trips[recommendedIndex]?.optionIndexes ?? null;
}

// ** Attachments of a point / area (mockup v1.38.78): any number of PDF, DOCX, JPG or PNG
// files, each up to 20 MB
export const SECURITY_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;
export const SECURITY_ATTACHMENT_FILE_TYPES: string[] = [
  "pdf",
  "jpg",
  "png",
  "jpeg",
  "docx",
];

// "report.PDF" → "pdf"; "" when the name has no extension
export function getSecurityAttachmentType(fileName: string): string {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex < 0 ? "" : fileName.slice(dotIndex + 1).toLowerCase();
}

// "1.4 MB", "820 KB" under the attached file's name
export function formatSecurityFileSize(sizeInBytes: number): string {
  if (sizeInBytes >= 1024 * 1024) {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(sizeInBytes / 1024))} KB`;
}

// Risk distance of an unsafe point of interest whose POI master has no
// unsafeRadiusInMeters: roads closer than this are unsafe (red)
export const SECURITY_UNSAFE_POI_DEFAULT_RADIUS_METERS = 100;

// Flag of a country in the check-in picker (developer's request 2026-10-16): the name as
// a slug ("Côte d'Ivoire" → "cote-divoire") against the tags of the flags API, as the
// Performance country filter, then SECURITY_STATIC_COUNTRY_FLAGS; with its code shown
// under the flag. null when no flag has that name
export function getSecurityCountryFlag(
  countryName: string,
  flags: SecurityCountryFlagModel[],
): SecurityCountryFlagResultModel | null {
  const slug = countryName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/['.]/g, "");
  if (!slug) return null;
  // A tag, or the flag's own code ("AUS & NZ" for the group "AUS & NZ")
  const code = countryName.trim().toLowerCase();
  const flag =
    flags.find((item) =>
      (item?.tags ?? []).some(
        (tag) => (tag ?? "").toLowerCase().trim() === slug,
      ),
    ) ??
    flags.find((item) => (item?.code ?? "").trim().toLowerCase() === code);
  const match = flag?.img
    ? { img: flag.img, code: flag.code }
    : SECURITY_STATIC_COUNTRY_FLAGS[slug];
  if (!match) return null;
  return {
    icon: `assets/img/country-flags/${match.img}`,
    code: match.code?.trim() || undefined,
  };
}

// Round placeholder of a check-in country without a flag (or whose flag file fails)
export const SECURITY_FALLBACK_COUNTRY_FLAG =
  "assets/img/country-flags/fallback-flag.svg";

// Flags of country groups / spellings the flags API may not tag (as the Performance
// country filter's STATIC_FLAG_MAP): slug → file in assets/img/country-flags/ + code
const SECURITY_STATIC_COUNTRY_FLAGS: Record<string, { img: string; code?: string }> =
  {
    "aus-&-nz": { img: "aus-&-nz.svg", code: "AUS & NZ" },
    "australia-&-new-zealand": { img: "aus-&-nz.svg", code: "AUS & NZ" },
    "slv-&-ni": { img: "slv-&-ni.svg", code: "SLV & NI" },
    "el-salvador-&-nicaragua": { img: "slv-&-ni.svg", code: "SLV & NI" },
    fwi: { img: "fwi.svg", code: "FWI" },
    "french-west-indies": { img: "fwi.svg", code: "FWI" },
    "ivory-coast": { img: "ivory-coast.svg", code: "CIV" },
    "cote-divoire": { img: "ivory-coast.svg", code: "CIV" },
    "central-america": { img: "central-america.svg" },
    "central-europe": { img: "central-europe.svg" },
  };

// Perimeter being drawn / edited in the point / area form (developer's request
// 2026-10-16): yellow border and light yellow fill, as the Initial site setup fence
export const SECURITY_PERIMETER_DRAFT_STROKE = "#FDD100";
export const SECURITY_PERIMETER_DRAFT_FILL = "#FFFF00";

// Centre of a perimeter (area centroid; the average of its points when it has no area),
// where the pin of a drawn risk area sits
export function getSecurityPolygonCenter(
  path: google.maps.LatLngLiteral[],
): google.maps.LatLngLiteral {
  let area = 0;
  let lat = 0;
  let lng = 0;
  path.forEach((current, index) => {
    const next = path[(index + 1) % path.length];
    const cross = current.lng * next.lat - next.lng * current.lat;
    area += cross;
    lng += (current.lng + next.lng) * cross;
    lat += (current.lat + next.lat) * cross;
  });
  if (Math.abs(area) < 1e-12) {
    return {
      lat: path.reduce((sum, point) => sum + point.lat, 0) / path.length,
      lng: path.reduce((sum, point) => sum + point.lng, 0) / path.length,
    };
  }
  return { lat: lat / (3 * area), lng: lng / (3 * area) };
}

// Save tapped with required fields missing: the first field marked
// [data-invalid] in the form (top to bottom) is scrolled into view
export function scrollToFirstInvalidField(
  container: HTMLElement | undefined,
): void {
  container
    ?.querySelector<HTMLElement>("[data-invalid]")
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
}

// Point / area name limit (mockup v1.38.78); routes keep SECURITY_NAME_MAX_LENGTH
export const SECURITY_LOCATION_NAME_MAX_LENGTH = 20;
// Radius slider range; the number box beside it goes up to SECURITY_RADIUS_MAX_METERS
export const SECURITY_RADIUS_SLIDER_MAX_METERS = 1000;
export const SECURITY_RADIUS_MAX_DECIMALS = 4;
// Files one point / area can keep (the backend allows the same)
export const SECURITY_ATTACHMENT_MAX_COUNT = 20;

// Typed radius in metres ("12,5" works too): greater than 0, at most
// SECURITY_RADIUS_MAX_METERS and 4 decimals; NaN when it is not valid
export function parseSecurityRadius(text: string): number {
  const normalized = text.trim().replace(",", ".");
  if (!/^\d+(\.\d*)?$/.test(normalized)) return Number.NaN;
  const decimals = normalized.split(".")[1] ?? "";
  const radius = Number(normalized);
  return radius > 0 &&
    radius <= SECURITY_RADIUS_MAX_METERS &&
    decimals.length <= SECURITY_RADIUS_MAX_DECIMALS
    ? radius
    : Number.NaN;
}

// Radius box text as typed: digits and the first "." / "," only, cut to 4 decimals
// ("12.34567" → "12.3456", "1a2" → "12")
export function limitSecurityRadiusText(text: string): string {
  const cleaned = text.replace(/[^\d.,]/g, "");
  const separatorIndex = cleaned.search(/[.,]/);
  if (separatorIndex < 0) return cleaned;
  const whole = cleaned.slice(0, separatorIndex);
  const decimals = cleaned
    .slice(separatorIndex + 1)
    .replace(/[.,]/g, "")
    .slice(0, SECURITY_RADIUS_MAX_DECIMALS);
  return `${whole}${cleaned[separatorIndex]}${decimals}`;
}
